import { Link, router } from "@inertiajs/react"
import {
  FileEdit,
  Globe,
  MoreHorizontal,
  Plus,
  Eye,
  Trash2,
  Volume2,
} from "lucide-react"
import { useMemo } from "react"
import camelcaseKeys from "camelcase-keys"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { Phrase } from "@/types/phrase"

interface Props {
  drafts: Phrase[]
  published: Phrase[]
}

export default function EditorialPhrasesIndex(props: unknown) {
  const data = useMemo(
    () => camelcaseKeys(props as Record<string, unknown>, { deep: true }) as Props,
    [props]
  )

  const { drafts, published } = data

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const handleDelete = (phrase: Phrase) => {
    if (confirm(`Delete "${phrase.content}"? This cannot be undone.`)) {
      router.delete(`/editorial/phrases/${phrase.slug}`)
    }
  }

  const PhraseRow = ({ phrase, showPublishDate = false }: { phrase: Phrase; showPublishDate?: boolean }) => {
    const isPublished = phrase.status === "published"
    const rowHref = isPublished ? `/phrases/${phrase.slug}` : `/editorial/phrases/${phrase.slug}/edit`

    const handleRowClick = (e: React.MouseEvent) => {
      // Don't navigate if clicking on the dropdown menu area
      if ((e.target as HTMLElement).closest("[data-dropdown-trigger]")) return

      router.visit(rowHref)
    }

    return (
      <div
        className="flex cursor-pointer items-center justify-between gap-4 border-b border-slate-100 py-4 transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
        onClick={handleRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleRowClick(e as unknown as React.MouseEvent)
          }
        }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate font-medium text-foreground">{phrase.content}</p>
            {phrase.audioUrl && (
              <Volume2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <p className="mt-1 truncate text-sm text-slate-600 dark:text-slate-400">
            {phrase.thaiText}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {showPublishDate && phrase.publishedAt
              ? `Published ${formatDate(phrase.publishedAt)}`
              : `Created ${formatDate(phrase.createdAt)}`}
          </p>
        </div>

      <div className="flex shrink-0 items-center gap-2">
        <Badge
          variant="secondary"
          className={cn(
            phrase.status === "published"
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
              : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
          )}
        >
          {phrase.status === "published" ? "Published" : "Draft"}
        </Badge>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-dropdown-trigger>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link href={`/editorial/phrases/${phrase.slug}/edit`}>
                <FileEdit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </DropdownMenuItem>
            {phrase.status === "published" && (
              <DropdownMenuItem asChild>
                <Link href={`/phrases/${phrase.slug}`} target="_blank">
                  <Eye className="mr-2 h-4 w-4" />
                  View Public Page
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleDelete(phrase)}
              className="text-red-600 focus:text-red-600 dark:text-red-400"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    )
  }

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Editorial", href: "/editorial" },
        { title: "Phrases", href: "/editorial/phrases" },
      ]}
    >
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Manage Phrases
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Create and publish phrases for the public phrase library
            </p>
          </div>
          <Link href="/conversations?hint=publish">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New from Conversation
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-full bg-amber-100 p-3 dark:bg-amber-900">
                <FileEdit className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Drafts</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {drafts.length}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-full bg-emerald-100 p-3 dark:bg-emerald-900">
                <Globe className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Published</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {published.length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Drafts Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileEdit className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              Drafts
              {drafts.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {drafts.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {drafts.length === 0 ? (
              <div className="py-8 text-center">
                <FileEdit className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
                <p className="text-sm text-muted-foreground">
                  No drafts yet. Create a phrase from a conversation to get started.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {drafts.map((phrase) => (
                  <PhraseRow key={phrase.id} phrase={phrase} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Published Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Published
              {published.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {published.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {published.length === 0 ? (
              <div className="py-8 text-center">
                <Globe className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
                <p className="text-sm text-muted-foreground">
                  No published phrases yet. Edit a draft and publish it to make it public.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {published.map((phrase) => (
                  <PhraseRow key={phrase.id} phrase={phrase} showPublishDate />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
